import React, { useState, useCallback } from 'react';
import { Accordion, Badge, Button, Table } from 'react-bootstrap';
import { FiCopy } from 'react-icons/fi';
import { writeClipboard } from '../../utils/weatherExport';

const formatForecastDate = (row) => {
    if (row?.forecast_day && row?.forecast_month && row?.forecast_year) {
        return `${row.forecast_day}/${row.forecast_month}/${row.forecast_year}`;
    }
    return 'N/A';
};

const buildTsv = (rows, valueHeader, valueFallback = '') => {
    const headers = ['Date', 'Heure', valueHeader];
    const dataRows = (rows || []).map((row) => {
        const date = row.forecast_day && row.forecast_month && row.forecast_year
            ? `${row.forecast_day}/${row.forecast_month}/${row.forecast_year}`
            : '';
        return [
            date,
            row.forecast_hour || '',
            row.value ?? valueFallback,
        ].join('\t');
    });
    return [headers.join('\t'), ...dataRows].join('\n');
};

const WeatherAccordionItem = ({
    eventKey,
    title,
    rows,
    valueHeader,
    dateHeader = 'Date',
    hourHeader = 'Heure',
    valueFallback = 'N/A',
    onCopy,
    copied = false,
    copyLabel = 'Copier le tableau',
    copiedLabel = 'Copié!',
    tableSize,
    bordered = false,
}) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    return (
        <Accordion.Item eventKey={eventKey} className="mb-2 border rounded overflow-hidden">
            <Accordion.Header>
                <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <span className="fw-semibold">{title}</span>
                    <Badge bg="secondary" pill className="ms-2">
                        {rows.length} ligne{rows.length > 1 ? 's' : ''}
                    </Badge>
                </div>
            </Accordion.Header>
            <Accordion.Body>
                {onCopy && (
                    <div className="d-flex justify-content-end mb-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onCopy();
                            }}
                            className="d-flex align-items-center"
                        >
                            <FiCopy className="me-2" />
                            {copied ? copiedLabel : copyLabel}
                        </Button>
                    </div>
                )}
                <div className="table-responsive">
                    <Table
                        striped
                        hover
                        bordered={bordered}
                        size={tableSize}
                        className="mb-0"
                    >
                        <thead>
                            <tr>
                                <th>{dateHeader}</th>
                                <th>{hourHeader}</th>
                                <th>{valueHeader}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={idx}>
                                    <td>{formatForecastDate(row)}</td>
                                    <td>{row.forecast_hour || 'N/A'}</td>
                                    <td>{row.value ?? valueFallback}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
};

/**
 * Affiche les tableaux météo (précipitations / température / vent) en accordéon.
 * enableCopy : boutons de copie TSV (Excel) pour précipitations et température.
 */
const WeatherDataSections = ({
    weatherData,
    weatherTemp,
    weatherWind,
    titlePrefix = 'Données météorologiques',
    precipitationTitle,
    temperatureTitle,
    windTitle,
    precipitationValueHeader = 'Nombre de mm de précipitations',
    dateHeader = 'Date',
    hourHeader = 'Heure',
    enableCopy = false,
    onCopyPrecipitation,
    onCopyTemperature,
    copied = false,
    copiedPrecipitation,
    copiedTemperature,
    copyLabel = 'Copier le tableau',
    copiedLabel = 'Copié!',
    tableSize,
    bordered = false,
    className = 'mb-3',
    idPrefix = 'weather',
}) => {
    const precip = Array.isArray(weatherData) ? weatherData : [];
    const temp = Array.isArray(weatherTemp) ? weatherTemp : [];
    const wind = Array.isArray(weatherWind) ? weatherWind : [];

    const [internalCopied, setInternalCopied] = useState({ precip: false, temp: false });

    const markCopied = useCallback((key) => {
        setInternalCopied((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => {
            setInternalCopied((prev) => ({ ...prev, [key]: false }));
        }, 2000);
    }, []);

    const handleCopyPrecip = useCallback(async () => {
        if (onCopyPrecipitation) {
            onCopyPrecipitation();
            return;
        }
        const ok = await writeClipboard(buildTsv(precip, precipitationValueHeader, '0,0'));
        if (ok) markCopied('precip');
    }, [onCopyPrecipitation, precip, precipitationValueHeader, markCopied]);

    const handleCopyTemp = useCallback(async () => {
        if (onCopyTemperature) {
            onCopyTemperature();
            return;
        }
        const ok = await writeClipboard(buildTsv(temp, 'Température (°C)'));
        if (ok) markCopied('temp');
    }, [onCopyTemperature, temp, markCopied]);

    if (precip.length === 0 && temp.length === 0 && wind.length === 0) {
        return null;
    }

    const showPrecipCopy = enableCopy || Boolean(onCopyPrecipitation);
    const showTempCopy = enableCopy || Boolean(onCopyTemperature);
    const precipCopied = Boolean(
        copiedPrecipitation || (onCopyPrecipitation && copied) || internalCopied.precip
    );
    const tempCopied = Boolean(
        copiedTemperature || (onCopyTemperature && copied) || internalCopied.temp
    );

    return (
        <Accordion className={`weather-data-accordion ${className}`.trim()}>
            <WeatherAccordionItem
                eventKey={`${idPrefix}-precipitation`}
                title={precipitationTitle || `${titlePrefix} - Précipitations`}
                rows={precip}
                valueHeader={precipitationValueHeader}
                dateHeader={dateHeader}
                hourHeader={hourHeader}
                valueFallback="0,0"
                onCopy={showPrecipCopy ? handleCopyPrecip : undefined}
                copied={!!precipCopied}
                copyLabel={copyLabel}
                copiedLabel={copiedLabel}
                tableSize={tableSize}
                bordered={bordered}
            />
            <WeatherAccordionItem
                eventKey={`${idPrefix}-temperature`}
                title={temperatureTitle || `${titlePrefix} - Température`}
                rows={temp}
                valueHeader="Température (°C)"
                dateHeader={dateHeader}
                hourHeader={hourHeader}
                onCopy={showTempCopy ? handleCopyTemp : undefined}
                copied={!!tempCopied}
                copyLabel={copyLabel}
                copiedLabel={copiedLabel}
                tableSize={tableSize}
                bordered={bordered}
            />
            <WeatherAccordionItem
                eventKey={`${idPrefix}-wind`}
                title={windTitle || `${titlePrefix} - Vent`}
                rows={wind}
                valueHeader="Vitesse du vent (km/h)"
                dateHeader={dateHeader}
                hourHeader={hourHeader}
                tableSize={tableSize}
                bordered={bordered}
            />
        </Accordion>
    );
};

export default WeatherDataSections;
